import { useEffect, useState } from 'react';
import { useBrandFilter } from '@/components/brand-filter/brand-filter-context';
import { getStoredSession } from '@/lib/auth-client';
import { groupLocationsByBrand, type ScopedLocationGroup } from '@/lib/scoped-locations';
import { isAggregateScope } from '@/lib/session-scope';
import type { SessionScope } from '@/types/auth';
import type { LocationResponseDto } from '@/types/brand';

/**
 * "Every branch" for the location Select. Radix rejects an empty string as an item value, so the
 * no-filter state needs a sentinel of its own. It is the default in aggregate, where pinning one
 * branch of one brand would hide most of what the operator came to see, and for a line session of a
 * multi-branch brand — the same default the inbox has always had. A single-branch brand stays pinned
 * to its one sucursal, so the (disabled) selector names it.
 */
export const ALL_LOCATIONS = 'all';

const SELECTED_LOCATION_STORAGE_KEY = 'orders:selectedLocationId';

/**
 * Where the branch pin is stored: one slot per login, shared with the header's pending-orders badge.
 * Every login on this browser shares localStorage (the dashboard also serves this app on its own
 * origin), and a sucursal login is forced onto its one branch — under a single key, the owner who
 * logged in next inherited that pin, and "Nuevo envío" offered that branch alone. Keyed by the brand
 * and the sucursal (or the whole brand); an aggregate session by its root master client.
 */
export function selectedLocationStorageKey(scope: SessionScope | null = getStoredSession()?.scope ?? null): string {
  if (!scope) return SELECTED_LOCATION_STORAGE_KEY;
  if (isAggregateScope(scope)) return `${SELECTED_LOCATION_STORAGE_KEY}:aggregate:${scope.rootMasterClientId}`;
  return `${SELECTED_LOCATION_STORAGE_KEY}:${scope.brandId}:${scope.locationId ?? 'brand'}`;
}

export interface LocationScope {
  selectedLocationId: string;
  setSelectedLocationId: (id: string) => void;
  /** Branch options: one group per brand in aggregate, a single unlabeled group otherwise. */
  locationGroups: ScopedLocationGroup[];
  /** A line session whose brand has several sucursales: "All branches" is offered and rows carry a chip. */
  multiBranch: boolean;
  /** There is something to choose between — the Select renders only then. */
  hasLocations: boolean;
  /** The pin to send to the API: undefined while "every branch" is selected. */
  locationIdParam: string | undefined;
}

/**
 * The branch pin shared by the orders surfaces (board and list): the same stored selection, the same
 * fallbacks when the brand filter or the session scope changes what exists. Kept in one place so the
 * two views cannot drift into pinning different branches from the same stored value.
 */
export function useLocationScope(locations: LocationResponseDto[]): LocationScope {
  const { isAggregate, brandFilterKey, scopedLocations } = useBrandFilter();
  const storageKey = selectedLocationStorageKey();
  const [selectedLocationId, setSelectedLocationId] = useState<string>(() => {
    return localStorage.getItem(storageKey) || ALL_LOCATIONS;
  });

  const multiBranch = !isAggregate && locations.length > 1;

  // Line sessions: a single-branch brand is pinned to its one sucursal; a multi-branch brand
  // keeps a stored pick that still exists and otherwise falls back to "every branch".
  useEffect(() => {
    if (isAggregate || locations.length === 0) return;
    setSelectedLocationId((current) => {
      if (locations.length === 1) return locations[0].id;
      if (current !== ALL_LOCATIONS && !locations.some((l) => l.id === current)) return ALL_LOCATIONS;
      return current;
    });
  }, [locations, isAggregate]);

  // Narrowing the brand filter changes which branches exist at all, and the one that was
  // pinned usually belongs to a brand that just left the view — fall back to "every branch".
  useEffect(() => {
    if (isAggregate) setSelectedLocationId(ALL_LOCATIONS);
  }, [brandFilterKey, isAggregate]);

  useEffect(() => {
    if (selectedLocationId) {
      localStorage.setItem(storageKey, selectedLocationId);
    }
  }, [selectedLocationId, storageKey]);

  const locationGroups: ScopedLocationGroup[] = isAggregate
    ? groupLocationsByBrand(scopedLocations)
    : [
        {
          brandId: '',
          brandName: '',
          locations: locations.map((l) => ({ id: l.id, name: l.name, brandId: '', brandName: '' })),
        },
      ];

  return {
    selectedLocationId,
    setSelectedLocationId,
    locationGroups,
    multiBranch,
    hasLocations: scopedLocations.length + locations.length > 0,
    locationIdParam: selectedLocationId && selectedLocationId !== ALL_LOCATIONS ? selectedLocationId : undefined,
  };
}
